import { ContactShadows, Environment, OrbitControls, Resize, useGLTF } from "@react-three/drei";
import { Canvas, useFrame } from "@react-three/fiber";
import { useEffect, useRef, useState } from "react";
import { subscribe, useSnapshot } from "valtio";
import { shoes } from "../config";
import { getPartName } from "../helpers/getPartName";
import { resolvePath } from "../helpers/resolvePath";
import { store } from "../store";

export function TheCanvas() {
  return (
    <Canvas shadows camera={{ position: [0, 0, 4], fov: 45 }} gl={{ preserveDrawingBuffer: true }} eventPrefix="client">
      <ambientLight intensity={0.7} />
      <spotLight intensity={0.5} angle={0.1} penumbra={1} position={[10, 15, 10]} castShadow />
      {Object.keys(shoes).map((type) => (
        <Resize width key={type} scale={1.5}>
          <Shoe type={type} />
        </Resize>
      ))}
      <Environment preset="city" />
      <ContactShadows position={[0, -0.8, 0]} opacity={0.25} scale={10} blur={1.5} far={0.8} />
      <OrbitControls minPolarAngle={Math.PI / 2} maxPolarAngle={Math.PI / 2} enableZoom={false} enablePan={false} />
    </Canvas>
  );
}
function Shoe({ type }) {
  const { modelUrl, components } = shoes[type];

  const ref = useRef();
  const snap = useSnapshot(store);
  const { nodes, materials } = useGLTF(modelUrl);
  const [hovered, set] = useState(null);

  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    ref.current.rotation.set(Math.cos(t / 4) / 8, Math.sin(t / 4) / 8, -0.2 - (1 + Math.sin(t / 1.5)) / 20);
    ref.current.position.y = (1 + Math.sin(t / 1.5)) / 10;
  });

  useEffect(
    () =>
      subscribe(store, () => {
        if (store.selectedShoeType === type) {
          ref.current.visible = true;
        } else {
          ref.current.visible = false;
        }
      }),
    [],
  );

  useEffect(() => {
    const cursor = `<svg width="128" height="128" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0)"><path fill="rgba(255, 255, 255, 0.5)" d="M29.5 54C43.031 54 54 43.031 54 29.5S43.031 5 29.5 5 5 15.969 5 29.5 15.969 54 29.5 54z" stroke="#000"/><g filter="url(#filter0_d)"><path d="M29.5 47C39.165 47 47 39.165 47 29.5S39.165 12 29.5 12 12 19.835 12 29.5 19.835 47 29.5 47z" fill="${
      snap.items[hovered]
    }"/></g><path d="M2 2l11 2.947L4.947 13 2 2z" fill="#000"/><text fill="#000" style="#fff-space:pre" font-family="Inter var, sans-serif" font-size="10" letter-spacing="-.01em"><tspan x="5" y="63">${getPartName(
      hovered,
    )}</tspan></text></g><defs><clipPath id="clip0"><path fill="#fff" d="M0 0h64v64H0z"/></clipPath><filter id="filter0_d" x="6" y="8" width="128" height="128" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feColorMatrix in="SourceAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"/><feOffset dy="2"/><feGaussianBlur stdDeviation="3"/><feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0"/><feBlend in2="BackgroundImageFix" result="effect1_dropShadow"/><feBlend in="SourceGraphic" in2="effect1_dropShadow" result="shape"/></filter></defs></svg>`;
    const auto = `<svg width="64" height="64" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="rgba(255, 255, 255, 0.5)" d="M29.5 54C43.031 54 54 43.031 54 29.5S43.031 5 29.5 5 5 15.969 5 29.5 15.969 54 29.5 54z" stroke="#000"/><path d="M2 2l11 2.947L4.947 13 2 2z" fill="#000"/></svg>`;
    if (hovered) {
      document.body.style.cursor = `url('data:image/svg+xml;base64,${btoa(cursor)}'), auto`;
      return () => (document.body.style.cursor = `url('data:image/svg+xml;base64,${btoa(auto)}'), auto`);
    }
  }, [hovered]);

  return (
    <group
      ref={ref}
      visible={store.selectedShoeType === type}
      onPointerOver={(e) => {
        e.stopPropagation();
        set(e.object.material.name);
      }}
      onPointerOut={(e) => e.intersections.length === 0 && set(null)}
      onPointerMissed={() => (store.current = null)}
      onClick={(e) => (e.stopPropagation(), (store.current = e.object.material.name))}>
      {
        /** base:
         * <mesh receiveShadow castShadow geometry={nodes.shoe.geometry} material={materials.laces} material-color={snap.items.laces}>
        <MeshOutlines thickness={5} color="#00d435" visible={hovered === materials.laces.name || store.current === materials.laces.name} />
      </mesh>
         */
        Object.keys(components).map((componentSlug) => {
          const { geometry } = components[componentSlug];
          return (
            <mesh
              key={componentSlug}
              receiveShadow
              castShadow
              geometry={resolvePath(nodes, geometry)}
              material={materials[componentSlug]}
              material-color={snap.items[componentSlug]}></mesh>
          );
        })
      }
    </group>
  );
}
